import type { DashboardOverview } from '../components/dashboard/DashboardStatsGrid';

const MODULE_JOB_STATS = [
  { module: 'transportation', totalKey: 'totalRides', completedKey: 'completedRides', cancelledKey: 'cancelledRides', activeKey: 'activeRides', pendingKey: 'pendingRides', assignmentKey: 'pendingAssignmentRides' },
  { module: 'food', totalKey: 'totalFoodOrders', completedKey: 'deliveredFoodOrders', cancelledKey: 'cancelledFoodOrders', activeKey: 'activeFoodOrders', pendingKey: 'pendingFoodOrders', assignmentKey: 'pendingAssignmentFoodOrders' },
  { module: 'grocery', totalKey: 'totalGroceryOrders', completedKey: 'deliveredGroceryOrders', cancelledKey: 'cancelledGroceryOrders', activeKey: 'activeGroceryOrders', pendingKey: 'pendingGroceryOrders', assignmentKey: 'pendingAssignmentGroceryOrders' },
  { module: 'porter', totalKey: 'totalPorterOrders', completedKey: 'completedPorterOrders', cancelledKey: 'cancelledPorterOrders', activeKey: 'activePorterOrders', pendingKey: 'pendingPorterOrders', assignmentKey: 'pendingAssignmentPorterOrders' },
  { module: 'on_demand', totalKey: 'totalOnDemandBookings', completedKey: 'completedOnDemandBookings', cancelledKey: 'cancelledOnDemandBookings', activeKey: 'activeOnDemandBookings', pendingKey: 'pendingOnDemandBookings', assignmentKey: 'pendingAssignmentOnDemandBookings' },
] as const;

const ROLE_MODULE_FOCUS: Record<string, readonly string[]> = {
  driver: ['transportation', 'porter'],
  delivery_partner: ['food', 'grocery', 'porter'],
  merchant: ['food', 'grocery'],
  service_provider: ['on_demand'],
};

const MODULE_LABELS: Record<string, string> = {
  transportation: 'Transport',
  food: 'Food',
  grocery: 'Grocery',
  porter: 'Porter',
  on_demand: 'Services',
};


type JobStatKey = keyof DashboardOverview;

function resolveModules(
  enabledModules: string[],
  isSuperAdmin?: boolean,
  role?: string,
): string[] {
  const roleFocus = role ? ROLE_MODULE_FOCUS[role] : undefined;
  const tenantModules = enabledModules.length
    ? enabledModules
    : MODULE_JOB_STATS.map((m) => m.module);

  if (isSuperAdmin) {
    return MODULE_JOB_STATS.map((m) => m.module);
  }

  if (roleFocus?.length) {
    return tenantModules.filter((module) => roleFocus.includes(module));
  }

  return tenantModules;
}

function roundRate(numerator: number, denominator: number): number {
  return denominator > 0 ? Math.round((numerator / denominator) * 10000) / 100 : 0;
}

export function formatPercent(value: number): string {
  return `${Number(value || 0).toFixed(2)}%`;
}

export interface ServiceStatusInput {
  count?: number;
  completed: number;
  cancelled: number;
  active: number;
  pending: number;
  other?: number;
}

export interface ServiceStatusSegment {
  key: string;
  label: string;
  count: number;
  rate: number;
  color: string;
}

const SERVICE_STATUS_COLORS = {
  completed: '#16a34a',
  cancelled: '#dc2626',
  active: '#0891b2',
  pending: '#ca8a04',
  other: '#64748b',
} as const;

/** Sum of displayed status buckets — should match the service total shown in analytics. */
export function getServiceAccountedTotal({
  count,
  completed,
  cancelled,
  active,
  pending,
  other,
}: ServiceStatusInput): number {
  const accounted = completed + cancelled + active + pending + (other ?? 0);
  const rawTotal = count ?? 0;
  if (rawTotal > accounted) return rawTotal;
  return accounted > 0 ? accounted : rawTotal;
}

/** Build status segments whose rates sum to 100% of the service total. */
export function buildServiceStatusSegments(
  input: ServiceStatusInput,
  labels?: Partial<Record<'completed' | 'active', string>>,
): ServiceStatusSegment[] {
  const total = getServiceAccountedTotal(input);
  const segments: Array<Omit<ServiceStatusSegment, 'rate'>> = [
    { key: 'completed', label: labels?.completed ?? 'Completed', count: input.completed, color: SERVICE_STATUS_COLORS.completed },
    { key: 'cancelled', label: 'Cancelled', count: input.cancelled, color: SERVICE_STATUS_COLORS.cancelled },
    { key: 'active', label: labels?.active ?? 'In progress', count: input.active, color: SERVICE_STATUS_COLORS.active },
    { key: 'pending', label: 'Pending', count: input.pending, color: SERVICE_STATUS_COLORS.pending },
  ];

  const otherCount = input.other ?? Math.max(0, total - segments.reduce((sum, seg) => sum + seg.count, 0));
  if (otherCount > 0) {
    segments.push({ key: 'other', label: 'Other', count: otherCount, color: SERVICE_STATUS_COLORS.other });
  }

  const withRates = segments.map((seg) => ({
    ...seg,
    rate: 0,
  }));

  return normalizeStatusRates(withRates, total);
}

/** Assign display rates in basis points so visible segments always sum to 100.00%. */
function normalizeStatusRates(segments: ServiceStatusSegment[], total: number): ServiceStatusSegment[] {
  if (!total || !segments.length) return segments;

  const bpsRows = segments.map((seg, idx) => ({
    idx,
    count: seg.count,
    bps: Math.round((seg.count / total) * 10000),
  }));
  const bpsSum = bpsRows.reduce((s, r) => s + r.bps, 0);
  let diff = 10000 - bpsSum;
  if (diff !== 0) {
    const targetIdx = bpsRows.reduce(
      (bestIdx, row, idx, arr) => (row.count > arr[bestIdx].count ? idx : bestIdx),
      0,
    );
    bpsRows[targetIdx].bps += diff;
    diff = 0;
  }

  return segments.map((seg, idx) => ({
    ...seg,
    rate: bpsRows[idx].bps / 100,
  }));
}

/** Sum segment rates without float drift (rates are assigned in basis points). */
export function sumServiceStatusRates(segments: ServiceStatusSegment[]): number {
  return segments.reduce((acc, seg) => acc + Math.round(seg.rate * 100), 0) / 100;
}

export function formatServiceStatusMixSummary(segments: ServiceStatusSegment[]): string {
  const visible = segments.filter((seg) => seg.count > 0);
  if (!visible.length) return 'No jobs yet';
  return visible.map((seg) => `${seg.count.toLocaleString()} ${seg.label.toLowerCase()} (${formatPercent(seg.rate)})`).join(' · ');
}

export function aggregateJobStats(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
) {
  const allowedModules = resolveModules(enabledModules, options?.isSuperAdmin, options?.role);
  const relevant = MODULE_JOB_STATS.filter((m) => allowedModules.includes(m.module));

  const totalJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.totalKey as JobStatKey]) || 0),
    0,
  );
  const completedJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.completedKey as JobStatKey]) || 0),
    0,
  );
  const cancelledJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.cancelledKey as JobStatKey]) || 0),
    0,
  );
  const activeJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.activeKey as JobStatKey]) || 0),
    0,
  );
  const pendingJobs = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.pendingKey as JobStatKey]) || 0),
    0,
  );

  const pendingByModule = relevant
    .map((m) => ({
      module: m.module,
      label: MODULE_LABELS[m.module] || m.module,
      count: Number(overview[m.pendingKey as JobStatKey]) || 0,
    }))
    .filter((row) => row.count > 0);

  const activeByModule = relevant
    .map((m) => ({
      module: m.module,
      label: MODULE_LABELS[m.module] || m.module,
      count: Number(overview[m.activeKey as JobStatKey]) || 0,
    }))
    .filter((row) => row.count > 0);

  const assignmentByModule = relevant
    .map((m) => ({
      module: m.module,
      label: MODULE_LABELS[m.module] || m.module,
      count: Number(overview[m.assignmentKey as JobStatKey]) || 0,
    }))
    .filter((row) => row.count > 0);

  const jobsNeedingAssignment = relevant.reduce(
    (sum, m) => sum + (Number(overview[m.assignmentKey as JobStatKey]) || 0),
    0,
  );

  return {
    totalJobs,
    completedJobs,
    cancelledJobs,
    activeJobs,
    pendingJobs,
    jobsNeedingAssignment,
    pendingByModule,
    activeByModule,
    assignmentByModule,
    completionRate: roundRate(completedJobs, totalJobs),
    cancelRate: roundRate(cancelledJobs, totalJobs),
  };
}

/** Sum per-service active counts — should match overview.activeJobs when all modules are enabled. */
export function sumActiveByService(overview: DashboardOverview = {}): number {
  return MODULE_JOB_STATS.reduce(
    (sum, m) => sum + (Number(overview[m.activeKey as JobStatKey]) || 0),
    0,
  );
}

export function sumPendingByService(overview: DashboardOverview = {}): number {
  return MODULE_JOB_STATS.reduce(
    (sum, m) => sum + (Number(overview[m.pendingKey as JobStatKey]) || 0),
    0,
  );
}

function coversAllServiceModules(
  enabledModules: string[],
  options?: { isSuperAdmin?: boolean; role?: string },
): boolean {
  const allowed = resolveModules(enabledModules, options?.isSuperAdmin, options?.role);
  return MODULE_JOB_STATS.every((m) => allowed.includes(m.module));
}

/** Prefer backend `jobsNeedingAssignment` when every service module is in scope. */
export function resolveJobsNeedingAssignment(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
): number {
  const fromParts = aggregateJobStats(overview, enabledModules, options).jobsNeedingAssignment;
  const fromApi = overview.jobsNeedingAssignment;
  if (fromApi == null || Number.isNaN(Number(fromApi))) return fromParts;
  if (coversAllServiceModules(enabledModules, options)) return Number(fromApi);
  return fromParts;
}

/** Prefer backend `pendingJobs` (dispatch buckets) when every service module is in scope. */
export function resolvePendingJobs(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
): number {
  const fromParts = aggregateJobStats(overview, enabledModules, options).pendingJobs;
  const fromApi = overview.pendingJobs;
  if (fromApi == null || Number.isNaN(Number(fromApi))) return fromParts;
  if (coversAllServiceModules(enabledModules, options)) return Number(fromApi);
  return fromParts;
}

/** Prefer backend `activeJobs` when every service module is in scope. */
export function resolveActiveJobs(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
): number {
  const fromParts = aggregateJobStats(overview, enabledModules, options).activeJobs;
  const fromApi = overview.activeJobs;
  if (fromApi == null || Number.isNaN(Number(fromApi))) return fromParts;
  if (coversAllServiceModules(enabledModules, options)) return Number(fromApi);
  return fromParts;
}

export function formatNeedsAssignmentLabel(count: number): string {
  return count === 1
    ? '1 job needs assignment'
    : `${count.toLocaleString()} jobs need assignment`;
}

export function formatInProgressAlertLabel(count: number): string {
  return count === 1
    ? '1 job in progress'
    : `${count.toLocaleString()} jobs in progress`;
}

export function formatPendingHint(
  pendingJobs: number,
  pendingByModule: Array<{ label: string; count: number }>,
): string {
  if (pendingJobs <= 0) return 'No jobs awaiting action';
  if (pendingByModule.length === 0) return `${pendingJobs.toLocaleString()} awaiting action`;
  return pendingByModule.map((row) => `${row.count.toLocaleString()} ${row.label}`).join(' · ');
}

export function formatNeedsAssignmentHint(
  count: number,
  assignmentByModule: Array<{ label: string; count: number }>,
): string {
  if (count <= 0) return 'No unassigned jobs in the action queue';
  if (assignmentByModule.length === 0) return `${count.toLocaleString()} awaiting partner assignment`;
  return assignmentByModule.map((row) => `${row.count.toLocaleString()} ${row.label}`).join(' · ');
}

export function formatActiveHint(
  activeJobs: number,
  activeByModule: Array<{ label: string; count: number }>,
): string {
  if (activeJobs <= 0) return 'No jobs in progress';
  if (activeByModule.length === 0) return `${activeJobs.toLocaleString()} in progress`;
  const breakdown = activeByModule.map((row) => `${row.count.toLocaleString()} ${row.label}`).join(' · ');
  return `${breakdown} (${activeJobs.toLocaleString()} total)`;
}

/** Status breakdown for dashboard "By service" cards — includes cancelled so totals reconcile. */
export function buildServiceCardDisplay(
  input: ServiceStatusInput & { count?: number },
  hintOptions?: { completedLabel?: string; activeLabel?: string },
): { value: number; hint: string } {
  const value = getServiceAccountedTotal(input);
  const bucketSum = input.completed + input.cancelled + input.active + input.pending + (input.other ?? 0);
  const other = input.other ?? Math.max(0, value - bucketSum);

  return {
    value,
    hint: formatServiceBreakdownHint({
      total: value,
      completed: input.completed,
      pending: input.pending,
      active: input.active,
      cancelled: input.cancelled,
      other,
      ...hintOptions,
    }),
  };
}

export function formatServiceBreakdownHint({
  total,
  completed,
  pending,
  active,
  cancelled,
  other: otherCount,
  completedLabel = 'completed',
  activeLabel = 'active',
}: {
  total?: number;
  completed: number;
  pending: number;
  active: number;
  cancelled: number;
  other?: number;
  completedLabel?: string;
  activeLabel?: string;
}): string {
  const segments: string[] = [];
  const push = (count: number, label: string) => {
    if (count > 0) segments.push(`${count.toLocaleString()} ${label}`);
  };

  push(completed, completedLabel);
  push(pending, 'pending');
  push(active, activeLabel);
  push(cancelled, 'cancelled');

  const accounted = completed + pending + active + cancelled;
  const other = otherCount ?? (total != null ? Math.max(0, total - accounted) : 0);
  push(other, 'other');

  if (total != null) {
    return segments.length
      ? `${segments.join(' · ')} (${total.toLocaleString()} total)`
      : `${total.toLocaleString()} total`;
  }

  return segments.join(' · ') || 'No rides yet';
}

/** Open workload per service — pending + in progress, same fields as "By service" cards. */
export function openJobsForModule(
  overview: DashboardOverview,
  module: (typeof MODULE_JOB_STATS)[number]['module'],
): number {
  const row = MODULE_JOB_STATS.find((m) => m.module === module);
  if (!row) return 0;
  const pending = Number(overview[row.pendingKey as JobStatKey]) || 0;
  const active = Number(overview[row.activeKey as JobStatKey]) || 0;
  return pending + active;
}

export function aggregateTodayActivity(
  overview: DashboardOverview = {},
  enabledModules: string[] = [],
  options?: { isSuperAdmin?: boolean; role?: string },
) {
  const allowedModules = resolveModules(enabledModules, options?.isSuperAdmin, options?.role);
  const relevant = MODULE_JOB_STATS.filter((m) => allowedModules.includes(m.module));

  const byModule = relevant
    .map((m) => ({
      module: m.module,
      label: MODULE_LABELS[m.module] || m.module,
      count: openJobsForModule(overview, m.module),
    }))
    .filter((row) => row.count > 0);

  const total = byModule.reduce((sum, row) => sum + row.count, 0);

  return { total, byModule };
}
